/**
 * 计划页 —— **F 域 F2 + G 域 G1/G2**（预算 · 周期账 · 未来 30 天必付）
 *
 * 依据：原型 `05-计划_预算与日历_原型.html`（该页副标题即「**预算 · 周期账 · 未来 30 天必付**」）
 * · F 域 PRD **§4.6.2**（F2 呈现）· G 域 PRD **§4.5 / §4.7 / §4.8 / §4.9** ·
 * `AC-F2.1`~`F2.10` · `AC-G1.8`~`G1.14` / `G1.17`~`G1.23` / `G1.28` / `G1.29`。
 *
 * ## 🔴 为什么预算与日历在**同一页**（而不是各开一页）
 * 导航只有 10 项（`DR-025` / 铁律 12），**没有「预算」这一项**；设计里它一直是「计划」页的一段。
 * ⇒ 本页 = 三段式：**预算段（本批新增）→ 未来必付 → 周期规则**。
 *
 * ## 🔴 端侧零口径（本页一个数都不算）
 *
 * | 显示 | 来源 |
 * |---|---|
 * | 卡上的已用 / 剩余 / 已用% / 时间进度 / 偏离 / 外推 / 档位 / 超支额 / 含待缴额 | 服务端 `progress.*` / `level` |
 * | 卡片**顺序** | 服务端已排好（总预算置顶 → 已超 > 接近 > 正常 → 已用% 降序）⇒ **不重排** |
 * | 待缴下钻的筛选 | 由 URL `?statuses=unpaid` 传给流水页（`AC-G1.14`） |
 * | 成员维度的可见性 | 服务端「单人账本 ⇒ 空数组」⇒ 端侧只判 `length === 0`（`AC-G1.23`） |
 *
 * ⚠️ **唯一的端侧算术是"条形宽度"**（成员条与进度条填充%）—— 它**不是金额口径**
 * （不参与任何显示金额的计算），且走共享白名单的 `divideExact`（端侧禁 `/`）。
 *
 * ## ⚠️ 本批的诚实边界
 * - **预算体检（`review`）**未实施（顺延下一批，见 changelog）⇒ 本页**不出现"复盘/体检"入口**
 *   （不画点不动的灰项）；
 * - **批量创建**服务端已可用（`AC-G1.25` 有集成用例），但**端侧多选 UI 顺延**（同一条纪律）；
 * - **成员维度**只做展示：不设上限、不排名、不出现"谁花得多"之类判断句。
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BudgetPeriod,
  UpcomingStatus,
  divideExact,
  type BudgetCardView,
  type ScheduleView,
  type UpcomingItemView,
} from '@jiaying/shared';
import {
  createBudget,
  deleteBudget,
  deleteSchedule,
  getBudgetSuggestions,
  getUpcoming,
  listAccounts,
  listBudgets,
  listCategories,
  listSchedules,
  updateBudget,
  updateSchedule,
} from '../api/endpoints.js';
import { localToday } from '../app/local-day.js';
import { BudgetCard } from '../ui/BudgetCard.js';
import { ScheduleDialog } from '../ui/ScheduleDialog.js';
import { BudgetForm } from '../ui/BudgetForm.js';
import { budgetDraftFromView, softHintFor } from '../features/budget-draft.js';
import { Amount } from '../ui/Amount.js';
import { Loading } from '../ui/Loading.js';
import {
  EMPTY_UPCOMING_HINT,
  EMPTY_UPCOMING_TITLE,
  FREQUENCY_LABELS,
  FX_INCOMPLETE_NOTE,
  SCHEDULE_KIND_LABELS,
  dueTextOf,
  kindLabelOf,
} from '../features/plan-labels.js';
import {
  EMPTY_BUDGET_HINT,
  EMPTY_BUDGET_TITLE,
  FX_INCOMPLETE_BUDGET_NOTE,
  MEMBER_SECTION_SUB,
  MEMBER_SECTION_TITLE,
  MEMBER_UNATTRIBUTED_LABEL,
  NO_BUDGET_HINT,
  NO_BUDGET_TITLE,
  SOFT_HINT_LEAD,
} from '../features/budget-labels.js';
import {
  budgetDraftIssue,
  emptyBudgetDraft,
  type BudgetDraft,
} from '../features/budget-draft.js';
import { yuanTextToCentsOrNull } from '../features/txn-draft.js';

/** 表单态（`null` = 未展开） */
interface FormState {
  mode: 'create' | 'edit';
  /** 编辑时的预算 id */
  id: string | null;
  draft: BudgetDraft;
}

/**
 * 到期状态的配色（🔴 遵铁律 8：**逾期是"预警"不是"负债状态"**）。
 * - 逾期 ⇒ `error.main`（朱红 = `--c-alert`，已超）
 * - 今天 ⇒ `warning.main`（琥珀 = `--c-notice`，需注意）
 * - 未来 ⇒ 中性（**不着色** —— 未来的固定支出不是"坏事"）
 */
function dueColor(item: UpcomingItemView): string {
  if (item.status === UpcomingStatus.Overdue) return 'error.main';
  if (item.status === UpcomingStatus.Today) return 'warning.main';
  return 'text.secondary';
}

export function PlanPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const upcoming = useQuery({ queryKey: ['upcoming'], queryFn: getUpcoming });
  const schedules = useQuery({ queryKey: ['schedules'], queryFn: listSchedules });
  const budgets = useQuery({ queryKey: ['budgets'], queryFn: listBudgets });
  const categories = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  /** 🆕 批次 95：绑定周期规则的候选账户（内部挂账：待摊 / 待缴 / 社保） */
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [form, setForm] = useState<FormState | null>(null);
  /** 🆕 批次 95：新建周期规则对话框 */
  const [ruleOpen, setRuleOpen] = useState(false);
  /**
   * 🆕 批 `128`：规则的**编辑 / 删除 / 启用二次确认**三态。
   *
   * 🔴 为什么「启用」要单独一个 state 而不是直接发请求（`D1` 决策）：
   * 停用的语义是「**跳过而非作废**」—— 引擎在停用期间**不推进水位**，
   * 所以重新启用会按水位把停用期间漏掉的期次**一次性补齐**（`schedule.integration.spec.ts` 钉着）。
   * 用户以为"点开就恢复"，实际可能一次冒出好几笔 ⇒ 必须让他先看见这句话再决定。
   */
  const [editRule, setEditRule] = useState<ScheduleView | null>(null);
  const [deleteRule, setDeleteRule] = useState<ScheduleView | null>(null);
  const [pendingEnable, setPendingEnable] = useState<ScheduleView | null>(null);
  /** 删除成功后的回报（`keptTransactionCount` 是服务端给的，端侧不猜） */
  const [removedRule, setRemovedRule] = useState<{ id: string; kept: number } | null>(null);
  const today = localToday();

  /**
   * 三档建议：**选了分类或总预算**时取。
   *
   * 🆕 批 `108`（负责人 2026-10-26：「预算建立可以提供建议，比如用前 3 个月的平均值」）：
   * 原先 `categoryId === TOTAL_OPTION_ID` 一律不取建议 —— 而**新建预算时用户最常先建总预算**，
   * 于是他看到的是一个没有任何参考的空表单。实际服务端**早就支持**：
   * `budgetSpentFrom(breakdown, BudgetTotalKey)` 会返回全部分类的支出合计
   * （`nucleus/budget.ts`，`budget.spec.ts` 里已有 `__total__ → 1000` 的断言）⇒ 端侧只需放开这个开关。
   *
   * ⚠️ `event` 周期仍不取（`AC-G1.19`：窗口由用户自定，服务端也返回全 `null`）。
   */
  const suggestCategoryId = form?.draft.categoryId ?? '';
  const suggestionEnabled = form !== null && suggestCategoryId !== '' && form.draft.period !== BudgetPeriod.Event;
  const suggestion = useQuery({
    queryKey: ['budget-suggestions', suggestCategoryId, form?.draft.period ?? ''],
    queryFn: () => getBudgetSuggestions(suggestCategoryId, form?.draft.period ?? BudgetPeriod.Monthly),
    enabled: suggestionEnabled,
  });

  const invalidateBudgets = (): void => {
    void qc.invalidateQueries({ queryKey: ['budgets'] });
  };

  const save = useMutation({
    mutationFn: (input: FormState) => {
      const all = categories.data?.items ?? [];
      const issue = budgetDraftIssue(input.draft, all);
      if (issue !== null) throw new Error(issue);
      const cents = yuanTextToCentsOrNull(input.draft.amountText);
      const payload = {
        amount: cents as number,
        startDate: input.draft.period === BudgetPeriod.Event ? input.draft.startDate : null,
        endDate: input.draft.period === BudgetPeriod.Event ? input.draft.endDate : null,
      };
      return input.mode === 'edit' && input.id !== null
        ? Promise.resolve(updateBudget(input.id, payload))
        : // 🔴 服务端「已存在 ⇒ 409」由 `save.error` 原样展示（端侧不造第二份判定）
          Promise.resolve(
            createBudget({
              categoryId: input.draft.categoryId,
              period: input.draft.period,
              ...payload,
            }),
          );
    },
    onSuccess: () => {
      setForm(null);
      invalidateBudgets();
    },
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      updateBudget(input.id, { enabled: input.enabled }),
    onSuccess: invalidateBudgets,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteBudget(id),
    onSuccess: invalidateBudgets,
  });

  const toggleRule = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      updateSchedule(input.id, { enabled: input.enabled }),
    onSuccess: () => {
      // 启停会改变"日历里有没有这一项" ⇒ 两个键都要失效（写路径影响读路径）
      void qc.invalidateQueries({ queryKey: ['schedules'] });
      void qc.invalidateQueries({ queryKey: ['upcoming'] });
    },
  });

  /**
   * 🆕 批 `128`：删规则（`AC-F1.9` —— **只删规则，已生成的流水保留**）。
   * 🔴 `keptTransactionCount` 由服务端算好下发；端侧**不按 `lastDoneDay` 反推笔数**（那是第二份口径）。
   */
  const removeRule = useMutation({
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: (view) => {
      setDeleteRule(null);
      setRemovedRule({ id: view.id, kept: view.keptTransactionCount });
      void qc.invalidateQueries({ queryKey: ['schedules'] });
      void qc.invalidateQueries({ queryKey: ['upcoming'] });
    },
  });

  /**
   * 启停入口的**唯一分派点**（`D1`）。
   *
   * - 关 ⇒ 直接停用（不确认：停用是可逆的，且当场就生效）；
   * - 开 ⇒ 若 `nextDueDay` **早于今天**，说明停用期间有欠账 ⇒ 先弹确认（可取消）。
   *
   * ⚠️ 判据只用服务端下发的两个日期（`nextDueDay` / 今天），**不推算会补几笔** ——
   * 精确笔数要 `dueDaysThrough` + 锚点日 + 水位，端侧重算就是第二份生成口径（铁律 24）。
   */
  const onRuleToggle = (rule: ScheduleView, next: boolean): void => {
    if (!next) {
      toggleRule.mutate({ id: rule.id, enabled: false });
      return;
    }
    const overdue = rule.nextDueDay !== null && rule.nextDueDay < today;
    if (overdue) {
      setPendingEnable(rule);
      return;
    }
    toggleRule.mutate({ id: rule.id, enabled: true });
  };

  // 🔴 `isPending` 与 `isError` 两条分开守（`isPending` 不排除"已失败"）
  if (upcoming.isPending || schedules.isPending || budgets.isPending || categories.isPending) {
    return <Loading />;
  }
  if (upcoming.isError || schedules.isError || budgets.isError || categories.isError) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        计划数据加载失败，请刷新重试。
      </Alert>
    );
  }

  const data = upcoming.data;
  const rules = schedules.data.items;
  const bl = budgets.data;
  const allCategories = categories.data.items;
  const totalCard = bl.items.find((c) => c.isTotal) ?? null;

  const issue = form === null ? null : budgetDraftIssue(form.draft, allCategories);
  /**
   * 🔴 软提示的判定在**纯逻辑层**（`features/budget-draft.ts` 的 `softHintFor`）——
   * 本批把它从页面搬出去，就是为了让"两个方向"都能**不渲染**直测（㉛ 批的三层范式）。
   */
  const formSoftHint =
    form === null
      ? null
      : softHintFor({
          categoryId: form.draft.categoryId,
          period: form.draft.period,
          amountText: form.draft.amountText,
          editingId: form.mode === 'edit' ? form.id : null,
          categorySum: bl.categorySum,
          totalAmount: bl.totalAmount,
          totalPeriod: totalCard?.period ?? null,
          items: bl.items,
        });

  return (
    <Stack spacing={2} data-testid="plan-page">
      {/* ═══════════════════ ① 周期规则（F 域 F1） ═══════════════════ */}
      {/*
        🔴 批 `128`：这一段**从页面最底部提到第一段**（负责人 2026-10-31：
        「周期记账需要可以编辑，停止，删除，并且位置放在显眼的位置，现在太下面了」）。
        段内顺序不变（标题 → 新建 → 列表），变的只是它相对预算 / 必付的位置。
      */}
      <Card data-testid="rules-section">
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                周期记账
              </Typography>
              <Typography variant="caption" color="text.secondary">
                周期规则：待缴 / 待摊每期生成待缴流水，「周期记账模板」到期直接记一笔已发生的收支 ·
                改规则只影响未来期次，删规则不删已生成的流水
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => setRuleOpen(true)}
              sx={{ flexShrink: 0 }}
              data-testid="schedule-new"
            >
              新建规则
            </Button>
          </Stack>

          {/*
            删除回报（`AC-F1.9`）：笔数由服务端给（`keptTransactionCount`），端侧**不反推**。
            这句话是"只删模板"的唯一证据 —— 没有它，用户会以为流水一起没了。
          */}
          {removedRule === null ? null : (
            <Alert
              severity="success"
              sx={{ mt: 1 }}
              onClose={() => setRemovedRule(null)}
              data-testid="rule-removed"
            >
              规则已删除；已生成的 <b>{removedRule.kept}</b> 笔流水保留在流水页。
            </Alert>
          )}

          {toggleRule.isError ? (
            <Alert severity="warning" sx={{ mt: 1 }} data-testid="rule-toggle-error">
              {toggleRule.error instanceof Error ? toggleRule.error.message : '操作失败，请重试。'}
            </Alert>
          ) : null}

          {rules.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="rules-empty">
              还没有周期规则。给「待缴 / 待摊」账户绑一条规则后，每期会自动生成待缴流水；
              也可以用「周期记账模板」让固定扣款 / 固定入账到期直接记一笔。
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {rules.map((rule) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  onToggle={(enabled) => onRuleToggle(rule, enabled)}
                  onEdit={() => setEditRule(rule)}
                  onDelete={() => setDeleteRule(rule)}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════ ② 预算段（G 域） ═══════════════════ */}

      <Card>
        <CardContent>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                预算
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalCard === null
                  ? '还没设总预算'
                  : `总预算 · ${periodPhrase(totalCard)} · 已用 ${cardUsedText(totalCard)}`}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => setForm({ mode: 'create', id: null, draft: emptyBudgetDraft() })}
              data-testid="budget-new"
            >
              新建预算
            </Button>
          </Stack>

          {totalCard === null ? null : (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline', mt: 1, flexWrap: 'wrap' }}>
              <Amount
                cents={totalCard.progress.spent}
                currency={bl.baseCurrency}
                variant="h5"
                colored={false}
              />
              <Typography variant="body2" color="text.secondary">
                /
              </Typography>
              <Amount cents={totalCard.amount} currency={bl.baseCurrency} variant="body1" colored={false} />
            </Stack>
          )}

          {/* 缺汇率 ⇒ **必须标注**（缺汇率不静默、不按 1:1 冒充） */}
          {bl.fxIncomplete ? (
            <Alert severity="info" sx={{ mt: 1 }} data-testid="budget-fx-incomplete">
              {FX_INCOMPLETE_BUDGET_NOTE}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {/* 软提示（`AC-G1.28`：分类之和 > 总预算 ⇒ **只提示不拦截**，且给两个具体数字） */}
      {bl.overAllocated && bl.totalAmount !== null ? (
        <Alert severity="info" data-testid="budget-over-allocated">
          {SOFT_HINT_LEAD}{' '}
          <Amount cents={bl.categorySum} currency={bl.baseCurrency} variant="caption" colored={false} />{' '}
          已超过总预算{' '}
          <Amount cents={bl.totalAmount} currency={bl.baseCurrency} variant="caption" colored={false} />
          。<b>仍可保存</b> —— 这是软提示、不拦你。
        </Alert>
      ) : null}

      {/* 表单（展开时） */}
      {form === null ? null : (
        <BudgetForm
          mode={form.mode}
          draft={form.draft}
          onChange={(draft) => setForm({ ...form, draft })}
          categories={allCategories}
          suggestion={suggestionEnabled && suggestion.isSuccess ? suggestion.data : undefined}
          issue={issue}
          softHint={formSoftHint}
          currency={bl.baseCurrency}
          saving={save.isPending}
          onSave={() => save.mutate(form)}
          onCancel={() => setForm(null)}
        />
      )}

      {/* 保存失败：🔴 直接展示服务端 `message`（端侧不编第二份文案） */}
      {save.isError ? (
        <Alert severity="warning" data-testid="budget-save-error">
          {save.error instanceof Error ? save.error.message : '保存失败，请重试。'}
        </Alert>
      ) : null}

      {/* 卡片列表（🔴 顺序由服务端定，端侧不重排） */}
      {bl.items.length === 0 ? (
        <Card data-testid="budget-empty">
          <CardContent>
            <Typography variant="subtitle1">{EMPTY_BUDGET_TITLE}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {EMPTY_BUDGET_HINT}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5} data-testid="budget-list">
          {bl.items.map((card) => (
            <BudgetCard
              key={card.id}
              card={card}
              onToggle={(enabled) => toggle.mutate({ id: card.id, enabled })}
              onDrillUnpaid={() => navigate('/journal?statuses=unpaid')}
              onEdit={() => setForm({ mode: 'edit', id: card.id, draft: budgetDraftFromView(card) })}
            />
          ))}
        </Stack>
      )}

      {/* 无总预算时的引导（**不显示数字** —— 与"不画灰项"同一纪律） */}
      {bl.items.length > 0 && totalCard === null ? (
        <Alert severity="info" data-testid="budget-no-total">
          <b>{NO_BUDGET_TITLE}</b>：{NO_BUDGET_HINT}
        </Alert>
      ) : null}

      {/* 成员维度（`AC-G1.21` / `G1.23`：单人账本 ⇒ 服务端给空数组 ⇒ 整块不渲染） */}
      {bl.members.length === 0 ? null : (
        <Card data-testid="budget-members">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {MEMBER_SECTION_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {MEMBER_SECTION_SUB}
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {bl.members.map((m) => (
                <Stack key={m.userId} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: 96 }} noWrap>
                    {m.name}
                    {m.isSelf ? '（我）' : ''}
                  </Typography>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'action.hover' }}>
                    <Box
                      data-testid={`member-bar-${m.userId}`}
                      sx={{
                        width: `${memberWidthPercent(m.spent as number, bl.members)}%`,
                        height: '100%',
                        borderRadius: 4,
                        bgcolor: 'primary.main',
                      }}
                    />
                  </Box>
                  <Amount cents={m.spent} currency={bl.baseCurrency} variant="body2" colored={false} />
                </Stack>
              ))}
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.75 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" data-testid="member-unattributed">
                    {MEMBER_UNATTRIBUTED_LABEL}
                  </Typography>
                  <Amount
                    cents={bl.unattributed}
                    currency={bl.baseCurrency}
                    variant="caption"
                    colored={false}
                  />
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 删除入口（🔴 只删预算行，不动流水 —— `AC-G1.27`） */}
      {bl.items.length === 0 ? null : (
        <Box data-testid="budget-actions">
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {bl.items.map((c) => (
              <Button
                key={c.id}
                size="small"
                color="inherit"
                title="只删预算本身，已产生的流水与历史不受影响"
                onClick={() => remove.mutate(c.id)}
                data-testid={`budget-remove-${c.id}`}
              >
                删除「{c.categoryName}」
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      {remove.isError ? (
        <Alert severity="warning" data-testid="budget-remove-error">
          {remove.error instanceof Error ? remove.error.message : '删除失败，请重试。'}
        </Alert>
      ) : null}

      {/* ═══════════════════ ③ 未来 30 天必付（F 域 F2） ═══════════════════ */}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            未来 30 天必付
          </Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline', mt: 0.5, flexWrap: 'wrap' }}>
            {/* 🔴 合计用服务端下发的 `totalCents`，端侧不求和 */}
            <Amount cents={data.totalCents} currency={data.baseCurrency} variant="h5" colored={false} />
            <Typography variant="caption" color="text.secondary">
              信用卡 <Amount cents={data.byKind.creditCard} currency={data.baseCurrency} variant="caption" colored={false} /> ·
              待缴 <Amount cents={data.byKind.payable} currency={data.baseCurrency} variant="caption" colored={false} /> ·
              周期账 <Amount cents={data.byKind.schedule} currency={data.baseCurrency} variant="caption" colored={false} />
            </Typography>
          </Stack>
          {data.fxIncomplete ? (
            <Alert severity="info" sx={{ mt: 1 }} data-testid="fx-incomplete">
              {FX_INCOMPLETE_NOTE}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {data.items.length === 0 ? (
        <Card data-testid="upcoming-empty">
          <CardContent>
            <Typography variant="subtitle1">{EMPTY_UPCOMING_TITLE}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {EMPTY_UPCOMING_HINT}
            </Typography>
            {/* 🔴 入口指向**真实存在**的页面（账户页）—— 不画点不动的灰按钮 */}
            <Box
              component="button"
              onClick={() => navigate('/accounts')}
              sx={{
                mt: 1,
                border: 'none',
                background: 'none',
                p: 0,
                cursor: 'pointer',
                color: 'primary.main',
                font: 'inherit',
              }}
            >
              去看看账户
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            {/* 🔴 不重排：顺序由服务端给（`AC-F2.4`） */}
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} spacing={1}>
              {data.items.map((item) => (
                <Stack
                  key={`${item.kind}-${item.id}`}
                  direction="row"
                  sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 1 }}
                  data-testid={`upcoming-${item.kind}-${item.id}`}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Chip size="small" label={kindLabelOf(item)} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: dueColor(item) }}>
                        {item.dueDate} · {dueTextOf(item)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {/* 🔴 逐项金额带**原币**（外币项不会被显示成人民币 —— `DR-036` / `AC-F2.9`） */}
                    <Amount cents={item.originalCents} currency={item.currency} variant="body2" colored={false} />
                    {/*
                      下钻（`AC-F2.8`）：落到**账户页**（规则与卡的所在地）。
                      ⚠️ 没有 `accountId` 的项（理论上不该出现）不给按钮 —— 不做假入口。
                    */}
                    {item.accountId === null ? null : (
                      <Box
                        component="button"
                        onClick={() => navigate('/accounts')}
                        data-testid={`drill-${item.kind}-${item.id}`}
                        sx={{
                          border: 'none',
                          background: 'none',
                          p: 0,
                          cursor: 'pointer',
                          color: 'primary.main',
                          font: 'inherit',
                          fontSize: '0.75rem',
                        }}
                      >
                        账户
                      </Box>
                    )}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ═══════════════════ 对话框区（规则：新建 / 编辑 / 删除 / 启用确认） ═══════════════════ */}

      {/*
        🔴 批 `132`：传**全量账户**，候选由对话框按 `kind` 分派
        （`features/schedule-candidates.ts`）—— 模板要能挂储蓄卡 / 信用卡 / 社保 / 投资账户。
      */}
      {ruleOpen && (
        <ScheduleDialog
          accounts={accounts.data?.items ?? []}
          categories={categories.data?.items ?? []}
          onClose={() => setRuleOpen(false)}
          onCreated={() => setRuleOpen(false)}
        />
      )}

      {/*
        🆕 批 `128` 编辑态。🔴 `key` 必须带规则 id ——
        否则同一个组件实例从 A 规则切到 B 规则时 `useState` 初值不重跑，
        表单会显示上一条规则的值（改错了还不知道自己在改哪条）。
      */}
      {editRule === null ? null : (
        <ScheduleDialog
          key={editRule.id}
          rule={editRule}
          accounts={accounts.data?.items ?? []}
          categories={categories.data?.items ?? []}
          onClose={() => setEditRule(null)}
          onCreated={() => setEditRule(null)}
        />
      )}

      {/* 删除确认：明示「只删模板、流水保留」（`AC-F1.9` 的界面明示） */}
      {deleteRule === null ? null : (
        <Dialog
          open
          onClose={() => setDeleteRule(null)}
          maxWidth="xs"
          fullWidth
          data-testid="rule-delete-dialog"
        >
          <DialogTitle>删除这条周期规则？</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                「{deleteRule.name ?? deleteRule.accountName ?? '未命名规则'}」 ·{' '}
                {SCHEDULE_KIND_LABELS[deleteRule.kind]} · {FREQUENCY_LABELS[deleteRule.frequency]}
              </Typography>
              <Alert severity="warning">
                <b>只删除规则本身</b>：已生成的流水<b>不会</b>被删除，在流水页照常可见。
                删除后这条规则不再生成任何新期次，<b>且无法恢复</b>（要恢复只能重新建一条）。
                如果只是想暂时不生成，用右侧开关<b>停用</b>即可。
              </Alert>
              {removeRule.isError && (
                <Alert severity="error">
                  {removeRule.error instanceof Error ? removeRule.error.message : '删除失败，请重试。'}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteRule(null)} disabled={removeRule.isPending}>
              取消
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={removeRule.isPending}
              onClick={() => removeRule.mutate(deleteRule.id)}
              data-testid="rule-delete-confirm"
            >
              {removeRule.isPending ? '删除中…' : '删除规则'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/*
        启用二次确认（`D1`）。
        🔴 不报"会补几笔" —— 精确笔数要锚点日 + 水位 + `dueDaysThrough`，端侧重算就是第二份生成口径。
        只陈述**已被服务端下发的两个日期**所证明的事实：下次到期日已经过去了。
      */}
      {pendingEnable === null ? null : (
        <Dialog
          open
          onClose={() => setPendingEnable(null)}
          maxWidth="xs"
          fullWidth
          data-testid="rule-enable-dialog"
        >
          <DialogTitle>启用后会补齐停用期间的期次</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                「{pendingEnable.name ?? pendingEnable.accountName ?? '未命名规则'}」的下次到期日是{' '}
                <b>{pendingEnable.nextDueDay ?? '（无）'}</b>，已经过去了。
              </Typography>
              <Alert severity="info">
                停用期间系统<b>不会</b>推进这条规则的水位 ⇒ 重新启用后，会把这段时间漏掉的期次
                <b>一次性补齐</b>（可能一次生成好几笔流水）。不想补就继续停用，或改成新规则。
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingEnable(null)} data-testid="rule-enable-cancel">
              先别启用
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                toggleRule.mutate({ id: pendingEnable.id, enabled: true });
                setPendingEnable(null);
              }}
              data-testid="rule-enable-confirm"
            >
              确认启用并补齐
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

/**
 * 单条规则行（名称 / 种类 / 频率 / 下次到期 / 金额 / **编辑 · 删除 · 启停**）
 *
 * 🆕 批 `128`：从"只有一个启停开关"补成**完整管理行**。
 * 依据：`schedule.controller.ts` 的 `PATCH` / `DELETE` **早就存在**，
 * 而端侧 `deleteSchedule` / `settleSchedule` 两个封装是**零调用的死导出**（铁律 53：没入口 = 没兑现）。
 *
 * 🔴 三个动作的语义差别必须让用户看得见（否则"停止"和"删除"在他眼里是一回事）：
 * - **停用**：可逆、已生成的流水保留、重新启用会补齐停用期间的期次；
 * - **编辑**：只改未来期次，已生成的流水一个字段都不动（`AC-F1.8`）；
 * - **删除**：不可逆、只删规则、流水保留（`AC-F1.9`）。
 */
function RuleRow({
  rule,
  onToggle,
  onEdit,
  onDelete,
}: {
  rule: ScheduleView;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
      data-testid={`rule-${rule.id}`}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {rule.name ?? rule.accountName ?? '未命名规则'}
          </Typography>
          {/* 停用态必须**显式可见** —— 只看开关颜色时，用户会以为规则已经没救了 */}
          {rule.enabled ? null : (
            <Chip size="small" variant="outlined" color="default" label="已停用" data-testid={`rule-off-${rule.id}`} />
          )}
          {/*
            🔴 批 `131`：**未绑账户**的规则（v1 迁移来的 / 账户被物理删除）。
            引擎见 `accountId = NULL` 直接跳过 ⇒ 这条规则**永远不出流水**。
            不在列表上标出来，用户只会看到"规则在、流水没有"，然后以为记账坏了。
          */}
          {rule.accountName === null ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label="未绑账户"
              title="这条规则没有绑定账户，不会生成任何流水"
              data-testid={`rule-orphan-${rule.id}`}
            />
          ) : null}
          {rule.enabled && rule.nextDueDay !== null && rule.nextDueDay < localToday() ? (
            <Chip size="small" variant="outlined" color="warning" label="有待补期次" />
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {SCHEDULE_KIND_LABELS[rule.kind]} · {FREQUENCY_LABELS[rule.frequency]}
          {rule.nextDueDay === null ? '' : ` · 下次 ${rule.nextDueDay}`}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
        {rule.currency === null ? null : (
          <Amount cents={rule.amount} currency={rule.currency} variant="body2" colored={false} />
        )}
        <Button size="small" onClick={onEdit} data-testid={`rule-edit-${rule.id}`}>
          编辑
        </Button>
        <Button size="small" color="error" onClick={onDelete} data-testid={`rule-delete-${rule.id}`}>
          删除
        </Button>
        {/* 停用保留已生成流水（`AC-F1.9`）—— 文案在 title 里说清楚，不靠用户猜 */}
        <Switch
          size="small"
          checked={rule.enabled}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid={`rule-toggle-${rule.id}`}
          title={
            rule.enabled
              ? '停用后不再生成新期次（已生成的流水保留；重新启用会补齐停用期间的期次）'
              : '启用（若停用期间漏了期次，会先弹确认再补齐）'
          }
        />
      </Stack>
    </Stack>
  );
}

// ─────────────────────────── 纯展示辅助（**不是金额口径**） ───────────────────────────

/** 周期的口语说法（只用于标题行的副标题） */
function periodPhrase(card: BudgetCardView): string {
  switch (card.period) {
    case BudgetPeriod.Monthly:
      return '本月';
    case BudgetPeriod.Quarterly:
      return '本季';
    case BudgetPeriod.Yearly:
      return '本年';
    default:
      return `${card.startDate ?? ''}–${card.endDate ?? ''}`;
  }
}

/** 已用% 的短文本（**整百分点**，仅供副标题一行；卡内的精确值由 `percentText` 出） */
function cardUsedText(card: BudgetCardView): string {
  return `${divideExact(card.progress.usedE4, 100)}%`;
}

/**
 * 成员条宽度 —— 🔴 **纯展示缩放**（以最大者为满格），**不是金额口径**：
 * 它不参与任何显示金额的计算，只是让条形可比。走白名单 `divideExact`（端侧禁 `/`）。
 */
function memberWidthPercent(spent: number, members: readonly { spent: unknown }[]): number {
  if (spent <= 0) return 0;
  let max = 0;
  for (const m of members) {
    const v = m.spent as number;
    if (v > max) max = v;
  }
  if (max <= 0) return 0;
  return divideExact(spent * 100, max);
}
